<template>
    <div>
        <template>
            <v-card>
                <v-card-title>
                    {{ $tc('Payment', 3) }}
                </v-card-title>

                <v-container>
                    <v-card class="pl-2 pr-2">
                        <FromToDateFilter/>

                        <v-row>

                                <v-col>
                                    <v-text-field
                                            v-model="search"
                                            :label="$tc('SearchForNameOnTicket')"
                                            single-line
                                            clearable
                                    ></v-text-field>
                                </v-col>
                                <v-col>
                                    <v-btn @click="onFilterChange">
                                        <v-icon left>
                                            search
                                        </v-icon>
                                        {{ $tc('Search') }}
                                    </v-btn>
                                </v-col>
                        </v-row>
                    </v-card>
                </v-container>

                <v-card class="pl-2 pr-2">

                    <v-row>
                        <v-col>
                            <v-btn @click="toggleAllExpanded"
                                   small
                            >
                                <v-icon left v-if="expandAll">
                                    expand_less
                                </v-icon>
                                <v-icon left v-else>
                                    expand_more
                                </v-icon>
                                {{ toggleExpandedPhrase }}
                            </v-btn>
                        </v-col>
                    </v-row>

                    <v-data-table
                            :loading="paymentsAreLoading"
                            :headers="headers"
                            :items="payments"
                            :items-per-page="itemsPerPage"
                            :page.sync="page"
                            :single-expand="false"
                            :expanded.sync="paymentsExpanded"
                            disable-sort
                            item-key="pk"
                            show-expand
                            hide-default-footer
                            class="elevation-1"
                            ref="paymentTable"
                    >
                        <template v-slot:item.datetime_of_payment="{ item }">
                            <span>{{ new Date(item.datetime_of_payment).toLocaleString() }}</span>
                        </template>
                        <template v-slot:item.amount="{ item }">
                            <span>{{ formatValueWithTwoDecimals(item.amount) }}</span>
                        </template>

                        <template v-slot:expanded-item="{ headers, item }">
                            <td :colspan="headers.length">
                                <v-simple-table>
                                    <template v-slot:default>
                                        <thead>
                                        <tr>
                                            <th class="text-left">
                                                {{ $tc('TicketType') }}
                                            </th>
                                            <th class="text-left">
                                                {{ $tc('ActivityTypeName') }}
                                            </th>
                                            <th class="text-left">
                                                {{ $tc('FirstName') }}
                                            </th>
                                            <th class="text-left">
                                                {{ $tc('LastName') }}
                                            </th>
                                            <th class="text-left">
                                                {{ $tc('DiscountCode') }}
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <tr
                                                v-for="ticket in item.tickets"
                                                :key="ticket.pk"
                                        >
                                            <td>{{ ticket.short_description_phrase }}</td>
                                            <td>{{ ticket.activity_type_name_phrase }}</td>
                                            <td>{{ ticket.first_name }}</td>
                                            <td>{{ ticket.last_name }}</td>
                                            <td>{{ ticket.discount_code }}</td>
                                        </tr>
                                        </tbody>
                                    </template>
                                </v-simple-table>
                            </td>
                        </template>

                        <template v-slot:item.actions="{ item }">
                            <v-tooltip left>
                                <template v-slot:activator="{ on }">
                                    <div v-on="on">
                                        <v-btn small
                                               :disabled="shouldDisableRefundButton(item)"
                                               @click="openRefundDialog(item)"
                                        >
                                            <v-icon
                                                    small
                                                    class="mr-2"
                                            >
                                                undo
                                            </v-icon>
                                            {{ $tc('Refund') }}
                                        </v-btn>
                                    </div>
                                </template>
                                <span>{{ getRefundTooltip(item) }}</span>
                            </v-tooltip>
                        </template>

                    </v-data-table>

                    <div class="text-center pt-2">
                        <v-pagination
                                v-model="page"
                                :length="pageCount"
                        ></v-pagination>
                    </div>

                </v-card>
            </v-card>
        </template>

        <v-dialog
                v-model="refundDialogIsOpen"
                max-width="350"
        >
            <v-card>
                <v-card-title class="headline grey lighten-2">
                    {{ $t('Refund') }}
                </v-card-title>
                <v-card-text v-if="selectedPayment != null"
                    class="mt-3"
                >
                    {{ refundDialogTitle }}
                </v-card-text>
                <v-divider></v-divider>

                <v-card-actions>
                    <v-btn
                            color="red"
                            dark
                            @click="refundDialogIsOpen = false"
                    >
                        {{ $t('No') }}
                    </v-btn>

                    <v-spacer></v-spacer>

                    <v-btn
                            color="green"
                            dark
                            @click="refund()"
                    >
                        <v-icon>done</v-icon>
                        {{ $t('Yes') }}
                    </v-btn>

                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-snackbar
                v-model="paymentSnackbarShouldBeShown"
                :timeout="5000"
                transition="slide-y-transition"
                :color="paymentSnackbarColor"
        >
            <v-row>
                <v-icon
                        color="white"
                >
                    {{ paymentSnackbarIcon }}
                </v-icon>
                <v-spacer></v-spacer>
                <span>
                    {{ paymentSnackbarMessage }}.
                </span>
                <v-spacer></v-spacer>
            </v-row>
        </v-snackbar>
    </div>
</template>

<script>
    import axios from "axios";
    import i18n from "../i18n";
    import {mapGetters, mapState} from "vuex";
    import FromToDateFilter from "@/components/FromToDateFilter";
    import {PaymentAlternativeChoice} from "@/core/enums/payment_alternative_choice";
    import FormatHelper from "../core/helpers/format-helper";

    export default {
        name: "Payments",
        components: {FromToDateFilter},
        mounted() {
            this.loadAllData();
        },
        data: () => ({
            show: true,
            paymentsAreLoading: true,
            refundDialogIsOpen: false,
            paymentSnackbarShouldBeShown: false,
            paymentSnackbarMessage: '',
            lastRefundRequestSucceded: true,
            selectedPayment: null,
            search: '',
            itemsPerPage: 1,
            page: 1,
            pageCount: 0,
            paymentsExpanded: [],
            expandAll: false,
            headers:
                [
                    {
                        text: i18n.t('DateTimeOfPayment'),
                        value: 'datetime_of_payment',
                    },
                    {
                        text: i18n.t('Amount'),
                        value: 'amount',
                    },
                    {
                        text: i18n.t('FirstName'),
                        value: 'paying_user__first_name',
                    },
                    {
                        text: i18n.t('LastName'),
                        value: 'paying_user__last_name',
                    },
                    {
                        text: i18n.t('Currency'),
                        value: 'currency__code',
                    },
                    {
                        text: i18n.t('PaymentMethod'),
                        value: 'payment_method_phrase',
                    },
                    {
                        text: i18n.t('Manage'),
                        value: 'actions',
                        sortable: false,
                    },
                ],
            payments: [],
        }),
        computed: {
            ...mapGetters('common', [
                'serverURL',
            ]),
            ...mapGetters('auth', [
                'isAuthenticated',
            ]),
            ...mapState('common', [
                'selectedArea',
                'selectedFromDate',
                'selectedToDate',
            ]),
            ...mapState('auth', [
                'userIsSuperUser',
            ]),
            toggleExpandedPhrase: function () {
                if (this.expandAll) {
                    return i18n.tc('CollapseAll');
                }
                else {
                    return i18n.tc('ExpandAll')
                }
            },
            refundDialogTitle: function () {
                // Show name of payer if it exists
                if (this.selectedPayment.paying_user__first_name) {
                    return i18n.tc("AreYouSureYouWantToRefundTo", 1,
                        {
                            amount: this.formatValueWithTwoDecimals(this.selectedPayment.amount),
                            currencyCode: this.selectedPayment.currency__code,
                            firstName: this.selectedPayment.paying_user__first_name,
                            lastName: this.selectedPayment.paying_user__last_name,
                        });
                }

                return i18n.tc("AreYouSureYouWantToRefund", 1,
                    {
                        amount: this.formatValueWithTwoDecimals(this.selectedPayment.amount),
                        currencyCode: this.selectedPayment.currency__code,
                    });
            },
            paymentSnackbarColor: function () {
                if (!this.lastRefundRequestSucceded) {
                    return 'warning';
                }

                return 'success';
            },
            paymentSnackbarIcon: function () {
                if (!this.lastRefundRequestSucceded) {
                    return 'error';
                }

                return 'done';
            },
        },
        watch: {
            isAuthenticated() {
                if (this.isAuthenticated) {
                    this.loadAllData();
                }
                else {
                    this.payments = [];
                }
            },
            selectedArea() {
                this.loadAllData();
            },
            selectedFromDate() {
                this.onFilterChange();
            },
            selectedToDate() {
                this.onFilterChange();
            },
            page() {
                if (this.isAuthenticated) {
                    this.loadAllData();
                }
            },
        },
        methods: {
            loadAllData() {
                if (this.isAuthenticated) {
                    this.loadPayments();
                }
            },
            loadPayments() {
                this.paymentsAreLoading = true;
                axios
                    .get(this.serverURL + '/get_payments/', {
                        params: {
                            page: this.page,
                            search_string: this.search,
                            from_date: this.selectedFromDate,
                            to_date: this.selectedToDate,
                        }
                    })
                    .then(response => {
                        this.payments = response.data.rows;
                        this.paymentsAreLoading = false;
                        this.itemsPerPage = response.data.items_per_page;
                        this.pageCount = response.data.number_of_pages;
                        this.page = response.data.page;
                    })
                    .catch(() => {
                        this.paymentsAreLoading = false;
                    });
            },
            shouldDisableRefundButton(payment) {
                return payment.has_been_refunded || payment.payment_method_enum === PaymentAlternativeChoice.free_payment;
            },
            // Refunds a payment
            refund() {
                this.paymentsAreLoading = true;
                axios
                    .get(this.serverURL + '/request_payment_refund/' + this.selectedPayment.pk)
                    .then(response => {
                        this.refundDialogIsLoading = true;
                        this.lastRefundRequestSucceded = true;
                        this.paymentSnackbarMessage = response.data.user_message;
                        this.paymentSnackbarShouldBeShown = true;

                        // If we wait fo a while here before reloading the table, the refund will hopefully have been processed
                        setTimeout(() => {
                                this.loadPayments();
                            },
                            5000
                        );
                    })
                    .catch(error => {
                        this.paymentsAreLoading = false;
                        this.lastRefundRequestSucceded = false;
                        this.paymentSnackbarMessage = error.response.data.user_message;
                        this.paymentSnackbarShouldBeShown = true
                    });

                this.refundDialogIsOpen = false;
            },
            openRefundDialog(payment) {
                console.log("Opening dialog..");
                this.selectedPayment = payment;
                this.refundDialogIsOpen = true;
            },
            onFilterChange() {
                this.page = 1;
                this.loadPayments();
            },
            // Expand or collapse all table rows
            toggleAllExpanded() {
                this.expandAll = ! this.expandAll;

                if (this.expandAll) {
                    this.paymentsExpanded = this.payments;
                }
                else {
                    this.paymentsExpanded = [];
                }
            },
            // Tooltip for the refund buttons
            getRefundTooltip(payment) {
                if (payment.has_been_refunded) {
                    return i18n.t('PaymentHasAlreadyBeenRefunded');
                }
                else if (payment.payment_method_enum === PaymentAlternativeChoice.free_payment) {
                    return i18n.t('PaymentHasNotBeenPaidThroughOutby')
                }

                return i18n.t('RefundsTheFullAmount')
            },
            formatValueWithTwoDecimals(value_to_format) {
                return FormatHelper.formatValueWithTwoDecimals(value_to_format);
            }
        }
    }
</script>

